// src/components/WildernessActionsPanel.js
import React, { useState, useMemo } from 'react';
import styles from './WildernessActionsPanel.module.css';
import { getAllMonstersForDisplay, isMonsterUnlocked, getUnlockRequirement } from '../data/monsterData'; 
import { getItemImageSrc } from '../utils/getItemImageSrc'; 
import { gameConfigData } from '../data/data'; // <<<--- ADDED THIS IMPORT

const localDisplayActionsConfig = {
  mineOre: { name: "Mine Ore", energy: 15, time: 5, icon: "/img/icons/mineSign.png" },
  chopWood: { name: "Chop Wood", energy: 10, time: 5, icon: "/img/icons/chop_wood_sign.png" },
  forage: { name: "Forage", energy: 5, time: 5, icon: "/img/icons/forageSign.png" },
  startFishing: { name: "Fish", energy: 0, time: 5, icon: "/img/icons/fishingSign.png" },
  dungeonMining: { name: "Dungeon Mines", energy: 10, time: 0, icon: "/img/icons/mineSign.png" },
  startFarming: { name: "Farming", energy: 0, time: 5, icon: "/img/icons/land_icon.png" }
};

// This function processes monsters from MonsterData.js for panel display
const getMonstersForPanelDisplay = (playerKillCounts) => {
    const allMonsters = getAllMonstersForDisplay(); // Fetches all monsters
    const monstersByTier = {};

    // Find the highest tier available in the data
    const maxTier = Math.max(...allMonsters.map(m => m.tier || 0));
    
    // Initialize all tiers from 0 to maxTier
    for (let i = 0; i <= maxTier; i++) {
        monstersByTier[i] = { regulars: [], boss: null };
    }
    
    // Populate with ALL monsters (no tier filtering)
    allMonsters.forEach(monster => {
        const tier = monster.tier !== undefined ? parseInt(monster.tier) : null;
        if (tier !== null) {
            if (!monstersByTier[tier]) { // Safeguard
                monstersByTier[tier] = { regulars: [], boss: null };
            }
            
            const isUnlocked = isMonsterUnlocked(monster.key, playerKillCounts);
            const unlockReq = getUnlockRequirement(monster.key);
            
            if (monster.isBoss) {
                monstersByTier[tier].boss = {
                    ...monster,
                    isUnlocked,
                    unlockRequirement: unlockReq
                };
            } else {
                monstersByTier[tier].regulars.push({
                    key: monster.key,
                    name: monster.name,
                    tier: tier,
                    imgPath: monster.imgPath,
                    isUnlocked,
                    unlockRequirement: unlockReq,
                    currentKills: playerKillCounts[monster.key] || 0
                });
            }
        }
    });
    return monstersByTier;
};


const WildernessActionsPanel = ({
  onPlayerAction,
  onSelectMonster,
  selectedMonsterKey,
  playerData,
  activeTab,
  selectedActivity,
  onSelectActivity
}) => {
  const [activeTier, setActiveTier] = useState(0); // Track active monster tier, start with T0
  
  // Memoize to avoid re-calculating on every render unless dependencies change
  const monstersByTierAndBoss = useMemo(() => {
    const playerKillCounts = playerData.monsterKills || {};
    return getMonstersForPanelDisplay(playerKillCounts);
  }, [playerData.monsterKills]);

 

  const sortedTiersToDisplay = Object.keys(monstersByTierAndBoss)
    .map(Number) // Convert keys to numbers for sorting
    .sort((a, b) => a - b)
    // Show ALL tiers now, no filtering
    .filter(tierNum => monstersByTierAndBoss[tierNum] && (monstersByTierAndBoss[tierNum].regulars.length > 0 || monstersByTierAndBoss[tierNum].boss));

  const renderGatheringContent = () => (
    <div className={styles.tabContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>🌿 Resource Gathering</h3>
        <div className={styles.buttonGrid}>
          {['mineOre', 'chopWood', 'forage'].map((actionKey) => {
            const actionDetails = localDisplayActionsConfig[actionKey];
            const isSelected = selectedActivity === actionKey;
            return (
              <button
                key={actionKey}
                onClick={() => onSelectActivity(actionKey)}
                className={`${styles.actionButton} ${isSelected ? styles.selectedAction : ''}`}
                title={`${actionDetails.name} (${actionDetails.energy}E, ${actionDetails.time}m)`}
              >
                <img src={actionDetails.icon} alt={actionDetails.name} className={styles.actionIcon}/>
                <span>{actionDetails.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderActivitiesContent = () => (
    <div className={styles.tabContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>🎣 Wilderness Activities</h3>
        <div className={styles.buttonGrid}>
          <button
            onClick={() => onSelectActivity('startFishing')}
            className={`${styles.actionButton} ${selectedActivity === 'startFishing' ? styles.selectedAction : ''}`}
            title={`${localDisplayActionsConfig.startFishing.name} (${localDisplayActionsConfig.startFishing.energy}E, ${localDisplayActionsConfig.startFishing.time}m)`}
          >
            <img src={localDisplayActionsConfig.startFishing.icon} alt={localDisplayActionsConfig.startFishing.name} className={styles.actionIcon}/>
            <span>{localDisplayActionsConfig.startFishing.name}</span>
          </button>
          <button
            onClick={() => onSelectActivity('dungeonMining')}
            className={`${styles.actionButton} ${selectedActivity === 'dungeonMining' ? styles.selectedAction : ''}`}
            title={`${localDisplayActionsConfig.dungeonMining.name} (${localDisplayActionsConfig.dungeonMining.energy}E)`}
          >
            <img src={localDisplayActionsConfig.dungeonMining.icon} alt={localDisplayActionsConfig.dungeonMining.name} className={styles.actionIcon}/>
            <span>{localDisplayActionsConfig.dungeonMining.name}</span>
          </button>
          <button
            onClick={() => onSelectActivity('startFarming')}
            className={`${styles.actionButton} ${selectedActivity === 'startFarming' ? styles.selectedAction : ''}`}
            title={`${localDisplayActionsConfig.startFarming.name} (${localDisplayActionsConfig.startFarming.energy}E, ${localDisplayActionsConfig.startFarming.time}m)`}
          >
            <img src={localDisplayActionsConfig.startFarming.icon} alt={localDisplayActionsConfig.startFarming.name} className={styles.actionIcon}/>
            <span>{localDisplayActionsConfig.startFarming.name}</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderCombatContent = () => (
    <div className={styles.tabContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>⚔️ Combat Encounters</h3>
        
        {/* Monster Tier Tabs */}
        <div className={styles.tierTabs}>
          {sortedTiersToDisplay.map((tierNum) => (
            <button
              key={tierNum}
              onClick={() => setActiveTier(tierNum)}
              className={`${styles.tierTab} ${activeTier === tierNum ? styles.activeTierTab : ''}`}
            >
              T{tierNum}
            </button>
          ))}
        </div>

        {/* Active Tier Content */}
        <div className={styles.tierContent}>
          {(() => {
            const tierData = monstersByTierAndBoss[activeTier];
            if (!tierData || (tierData.regulars.length === 0 && !tierData.boss)) {
              return <p className={styles.emptyMessage}>No encounters available for Tier {activeTier}.</p>;
            }
            return (
              <>
                {tierData.regulars.length > 0 && (
                  <div className={styles.monsterGrid}>
                    {tierData.regulars.map(monster => (
                      <div key={monster.key} className={styles.monsterContainer}>
                        <button
                          className={`${styles.monsterButton} 
                                      ${selectedMonsterKey === monster.key ? styles.selectedMonster : ''} 
                                      ${!monster.isUnlocked ? styles.lockedMonster : ''}`}
                          onClick={() => monster.isUnlocked && onSelectMonster(monster.key)}
                          title={monster.isUnlocked ? monster.name : `Locked: ${monster.unlockRequirement?.monsterName} (${monster.unlockRequirement?.killCount} kills)`}
                          disabled={!monster.isUnlocked}
                        >
                          <img src={getItemImageSrc(monster.imgPath)} alt={monster.name} className={styles.monsterIcon} />
                          {!monster.isUnlocked && <div className={styles.lockOverlay}>🔒</div>}
                          {!monster.isUnlocked && monster.unlockRequirement && !monster.unlockRequirement.isTierBoss && (
                            <div className={styles.requirementCounter}>{monster.unlockRequirement.killCount}</div>
                          )}
                        </button>
                        {monster.isUnlocked && monster.currentKills > 0 && (
                          <div className={styles.killsCounter}>{monster.currentKills}</div>
                        )}
                        {!monster.isUnlocked && monster.unlockRequirement && monster.unlockRequirement.isTierBoss && (
                          <div className={styles.unlockProgress}>
                            Defeat Boss
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {tierData.boss && (
                  <button
                    onClick={() => onPlayerAction('challengeTierBoss', { tier: activeTier, bossKey: tierData.boss.key })}
                    className={styles.challengeBossButton}
                    title={`Challenge ${tierData.boss.name}`}
                    disabled={playerData.energy.current < (gameConfigData.actions.fightMonster?.energyCost || 5) * 2}
                  >
                    Challenge {tierData.boss.name} (Tier {activeTier} Boss)
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch(activeTab) {
      case 'gathering':
        return renderGatheringContent();
      case 'activities':
        return renderActivitiesContent();
      case 'combat':
        return renderCombatContent();
      default:
        return renderGatheringContent();
    }
  };

  return (
    <div className={styles.actionsPanelContainer}>
      {renderTabContent()}
    </div>
  );
};

export default WildernessActionsPanel;
